import React, { useState, createContext, useEffect, useContext } from "react";
import axios from "axios";
import { BACKEND_URL } from "../../config/config";
import { AuthContext } from "./AuthProviderWrapper";

const DeclarationContext = createContext();

function DeclarationProviderWrapper(props) {
  const { activeCompany } = useContext(AuthContext);
  const [declaration, setDeclaration] = useState();
  const [dsnFile, setDsnFile] = useState(null);
  const [availablePeriods, setAvailablePeriods] = useState(null);
  const [periodsForSelects, setPeriodsForSelects] = useState(null);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [periodsError, setPeriodsError] = useState(null);

  useEffect(() => {
    updateDeclaration();
  }, []);

  useEffect(() => {
    if (activeCompany?.membershipCode) {
      refreshAvailablePeriods();
      refreshPeriodsForSelects();
    }
  }, [activeCompany?.membershipCode]);

  const updateDeclaration = (newDeclaration) => {
    setDeclaration(newDeclaration);
  };

  const removeDeclaration = () => {
    setDeclaration(null);
    setDsnFile(null);
  };

  const updateDsnFile = (file) => {
    setDsnFile(file);
  };

  const removeDsnFile = () => {
    setDsnFile(null);
  };

  // Helper method to check if amount is zero (handles various formats)
  const isZeroAmount = (amount) => {
    if (amount === null || amount === undefined) return true;

    return amount === 0 ||
           amount === "0,00 €" ||
           amount === "0,00€" ||
           amount === "0.00 €" ||
           amount === "0.00€" ||
           parseFloat(String(amount).replace(',', '.')) === 0;
  };

  // Helper method to check if workforce is zero
  const isZeroWorkForce = (workForce) => {
    return parseInt(workForce || '0', 10) === 0;
  };

  // Main validation method for attestation (both amount and workforce must be zero)
  const isAttestation = (declaration) => {
    if (!declaration) return false;

    const feeAmount = declaration.feeAmount || 0;
    const workForce = declaration.workForce;

    return isZeroAmount(feeAmount) && isZeroWorkForce(workForce);
  };

  // Validation method for invalid combinations
  const getValidationError = (declaration) => {
    if (!declaration) return null;

    const feeAmount = declaration.feeAmount || 0;
    const workForce = declaration.workForce;

    const hasZeroAmount = isZeroAmount(feeAmount);
    const hasZeroWorkForce = isZeroWorkForce(workForce);

    if (hasZeroAmount && !hasZeroWorkForce) {
      // Case 3: Zero amount but non-zero workforce
      return "Une déclaration à montant nul doit avoir 0 effectif déclaré.";
    }

    if (!hasZeroAmount && hasZeroWorkForce) {
      // Case 4: Non-zero amount but zero workforce
      return "Une déclaration avec effectif = 0 doit avoir un montant nul.";
    }

    return null; // No validation error
  };

  const refreshAvailablePeriods = async () => {
    if (!activeCompany?.membershipCode) {
      return;
    }

    setPeriodsLoading(true);
    setPeriodsError(null);

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/declaration/available-periods/${activeCompany.membershipCode}`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.data?.data?.availablePeriods) {
        setAvailablePeriods(response.data.data.availablePeriods);
      }
    } catch (error) {
      console.error("Error fetching available periods:", error);
      setPeriodsError(error.message || "Failed to fetch available periods");
      setAvailablePeriods({
        monthly: {},
        semiAnnual: {},
        annual: []
      });
    } finally {
      setPeriodsLoading(false);
    }
  };

  const refreshPeriodsForSelects = async () => {
    if (!activeCompany?.membershipCode) {
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/declaration/periods-for-selects/${activeCompany.membershipCode}`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.data?.data?.periodsForSelects) {
        setPeriodsForSelects(response.data.data.periodsForSelects);
      }
    } catch (error) {
      console.error("Error fetching periods for selects:", error);
      setPeriodsForSelects({
        monthly: { years: [], months: {} },
        semiAnnual: { years: [], semesters: {} },
        annual: { years: [] }
      });
    }
  };

  return (
    <DeclarationContext.Provider
      value={{
        declaration,
        setDeclaration,
        updateDeclaration,
        removeDeclaration,
        dsnFile,
        updateDsnFile,
        removeDsnFile,
        isAttestation,
        isZeroAmount,
        isZeroWorkForce,
        getValidationError,
        availablePeriods,
        periodsForSelects,
        periodsLoading,
        periodsError,
        refreshAvailablePeriods,
        refreshPeriodsForSelects,
      }}
    >
      {props.children}
    </DeclarationContext.Provider>
  );
}

export { DeclarationProviderWrapper, DeclarationContext };
